import React, { createContext, useContext, useState } from "react";
import {
  TransformedDeliveryService,
  TransformedExtendedWarranty,
} from "./utils/serviceMapper";
import { emitServices } from "./utils/emitServices";

type Props = {
  children: React.ReactNode;
};

type ContextProviderProps = {
  resetSelectedServices: () => void;
  toggleSelectedService: (serviceId: string) => void;
  services: (TransformedDeliveryService | TransformedExtendedWarranty)[];
  setServices: React.Dispatch<
    React.SetStateAction<
      (TransformedDeliveryService | TransformedExtendedWarranty)[]
    >
  >;
};
export const ServicesAndWarrantiesContext =
  createContext<ContextProviderProps | null>(null);

export const ServicesAndWarrantiesContextProvider = ({ children }: Props) => {
  const [services, setServices] = useState<
    (TransformedDeliveryService | TransformedExtendedWarranty)[]
  >([]);

  const resetSelectedServices = () => {
    setServices((prevState) =>
      prevState.map((service) => {
        return { ...service, selected: false };
      }),
    );
    emitServices([]);
  };

  const toggleSelectedService = (serviceId: string) => {
    const newServices = [] as (
      | TransformedDeliveryService
      | TransformedExtendedWarranty
    )[];

    for (const service of services) {
      if (service.id === serviceId) {
        newServices.push({ ...service, selected: !service.selected });
      } else {
        newServices.push(service);
      }
    }

    setServices(newServices);

    const selectedServices = newServices.filter((service) => service.selected);

    emitServices(selectedServices);
  };

  return (
    <ServicesAndWarrantiesContext.Provider
      value={{
        services,
        setServices,
        toggleSelectedService,
        resetSelectedServices,
      }}
    >
      {children}
    </ServicesAndWarrantiesContext.Provider>
  );
};

export const useServicesAndWarrantiesContext = () => {
  const context = useContext(ServicesAndWarrantiesContext);

  if (!context) {
    throw new Error(
      "useServicesAndWarrantiesContext must be used within a ServicesAndWarrantiesContextProvider",
    );
  }

  return context;
};
